import { publicApi } from "@api";
import { envConfig } from "@configs/env";
import { withPosthogDistinctId } from "@configs/post-hog/distinct-id";
import { isPosthogAvailable } from "@configs/post-hog/is-available";
import { posthogReady } from "@configs/post-hog/posthog-ready";
import { QUERY_ALL } from "@constants";
import { Paths } from "@constants/paths";
import { call, delay, race, select, spawn } from "@redux-saga/core/effects";
import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query";
import logger from "@utils/logger";
import { execQuery } from "@utils/saga";
import { buildFilterQueries, initialGameQuery } from "@views/HomePage/components/GameList/query";
import { CATEGORY_POPULAR_SLUG } from "@views/HomePage/constants/category";
import { GameCategoryModel } from "kz-ui-sdk";

const POSTHOG_READY_TIMEOUT_MS = 5000;

const gameListArgsKey = (args: unknown) =>
  defaultSerializeQueryArgs({ queryArgs: args, endpointName: "getPublicGameListV3", endpointDefinition: {} as never });

let prefetchedGameListKey: string | null = null;

/** True once, when GameList asks for exactly the page the prefetch loaded. */
export const takePrefetchedGameList = (args: unknown) => {
  if (prefetchedGameListKey === null || gameListArgsKey(args) !== prefetchedGameListKey) return false;
  prefetchedGameListKey = null;
  return true;
};

// Built like GameList's first query so both share one cache entry.
const popularListArgs = (category: GameCategoryModel) => ({
  query: withPosthogDistinctId(buildFilterQueries(initialGameQuery, { category })),
  accountId: envConfig.accountId,
});

/** Starts home data while the app is still mounting; the home hooks pick it up from the RTK Query cache. */
export function* prefetchHomeData() {
  try {
    if (window.location.pathname !== Paths.PRIVATE.HOME) return;

    // Detached so an error in either cannot abort this saga or its parents.
    yield spawn(execQuery, publicApi.endpoints.getPublicGameProviderList.initiate({ query: QUERY_ALL }));
    yield spawn(execQuery, publicApi.endpoints.getPublicGameProviderStats.initiate());
    const categories: { entries?: GameCategoryModel[] } | undefined = yield call(
      execQuery,
      publicApi.endpoints.getPublicGameCategoryList.initiate({ query: QUERY_ALL }),
    );

    // A non-popular default category also needs a default provider; that path stays with the page.
    const popular = categories?.entries?.find((category) => category.slug === CATEGORY_POPULAR_SLUG);
    if (!popular) return;

    // The list query carries the PostHog distinct id, which exists only after init.
    if (isPosthogAvailable) {
      const { timeout }: { timeout?: unknown } = yield race({
        ready: call(() => posthogReady),
        timeout: delay(POSTHOG_READY_TIMEOUT_MS),
      });
      // GameList cannot render before PostHog is ready, so it would send a distinct id this request lacks.
      if (timeout) return;
    }

    const args = popularListArgs(popular);
    // If GameList already requested this page, our initiate is a no-op and must not claim it.
    const entry: { isUninitialized: boolean } = yield select(publicApi.endpoints.getPublicGameListV3.select(args));
    if (entry.isUninitialized) prefetchedGameListKey = gameListArgsKey(args);
    yield call(execQuery, publicApi.endpoints.getPublicGameListV3.initiate(args));
  } catch (error) {
    // Best-effort prefetch; an uncaught throw here would cancel the root saga.
    logger._console.warn("Error prefetching home data:", error);
  }
}
